import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Loader2, LogOut, Download, Clock, Check, X, Code, Zap, Settings, Copy, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessDashboard() {
    const { user, logout } = useAuth();
    const [leads, setLeads] = useState([]);
    const [settings, setSettings] = useState(null);
    const [bestPractices, setBestPractices] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("leads");
    const [saving, setSaving] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [expiringLeads, setExpiringLeads] = useState([]);
    const [sendingCampaign, setSendingCampaign] = useState(null);

    const userId = user?.user_id || user?.id || "";

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [leadsRes, settingsRes] = await Promise.all([
                API.get("/business/leads", { withCredentials: true }),
                API.get("/business/settings", { withCredentials: true }),
            ]);
            setLeads(leadsRes.data.leads || []);
            setSettings(settingsRes.data);

            const bpRes = await API.get(`/business/best-practices?industry=${settingsRes.data?.industry || "automotive"}`);
            setBestPractices(bpRes.data);

            const [campRes, expRes] = await Promise.all([
                API.get("/business/campaigns", { withCredentials: true }).catch(() => ({ data: { campaigns: [] } })),
                API.get("/business/expiring-leads?days_until_expiry=60", { withCredentials: true }).catch(() => ({ data: { leads: [] } })),
            ]);
            setCampaigns(campRes.data.campaigns || []);
            setExpiringLeads(expRes.data.leads || []);
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const saveSettings = async (updates) => {
        const newSettings = { ...settings, ...updates };
        setSettings(newSettings);
        setSaving(true);
        try {
            await API.put("/business/settings", {
                business_name: newSettings.business_name || "",
                industry: newSettings.industry || "automotive",
                tiers: newSettings.tiers || [],
                marketing_window_days: newSettings.marketing_window_days || 90,
                response_mode: newSettings.response_mode || "manual",
                listing_name: newSettings.listing_name || "",
                list_price: newSettings.list_price || 0,
            }, { withCredentials: true });
            toast.success("Settings saved");
        } catch { toast.error("Failed to save"); }
        setSaving(false);
    };

    const applyTemplate = async (industry) => {
        try {
            const res = await API.post("/business/apply-template", { industry }, { withCredentials: true });
            toast.success(`Applied ${industry} template. ${res.data.reason}`);
            setSettings(s => ({ ...s, industry, tiers: res.data.tiers, marketing_window_days: res.data.marketing_window_days }));
            const bpRes = await API.get(`/business/best-practices?industry=${industry}`);
            setBestPractices(bpRes.data);
        } catch { toast.error("Failed to apply template"); }
    };

    const exportCSV = () => {
        const headers = "Name,Email,Phone,Tier,Discount,Status,Opt-In Expires,Created\n";
        const rows = leads.map(l => `${l.name},${l.email},${l.phone},${l.tier},${l.discount_description || ""},${l.status},${l.opt_in_expires},${l.created_at}`).join("\n");
        const blob = new Blob([headers + rows], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "irish-goodbye-leads.csv"; a.click();
    };

    const copyEmbed = () => {
        const code = `<div id="irish-goodbye-widget" data-business-id="${userId}" data-theme="dark"></div>\n<script src="https://irishgoodbye.io/widget.js"></script>`;
        navigator.clipboard.writeText(code);
        toast.success("Embed code copied to clipboard!");
    };

    const createCampaign = async () => {
        try {
            await API.post("/business/campaigns", {
                name: "Consent Renewal",
                renewal_offer: "$10 off your next service",
                renewal_window_days: 90,
                trigger_days_before_expiry: 30,
                target_tiers: [1, 2, 3],
                active: true
            }, { withCredentials: true });
            toast.success("Campaign created");
            fetchAll();
        } catch { toast.error("Failed to create campaign"); }
    };

    const sendCampaign = async (campaignId) => {
        setSendingCampaign(campaignId);
        try {
            const res = await API.post(`/business/campaigns/${campaignId}/send`, {}, { withCredentials: true });
            toast.success(res.data.message);
            fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
        setSendingCampaign(null);
    };

    const statusColors = {
        new: "bg-blue-500/20 text-blue-400", contacted: "bg-yellow-500/20 text-yellow-400",
        quoted: "bg-[#047857]/20 text-[#047857]", sold: "bg-green-500/20 text-green-400",
        lost: "bg-red-500/20 text-red-400", revoked: "bg-[#6B7280]/20 text-[#6B7280]",
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#047857]" /></div>;

    const embedCode = `<!-- Irish Goodbye Consent Widget -->\n<div id="irish-goodbye-widget"\n     data-business-id="${userId}"\n     data-theme="dark">\n</div>\n<script src="https://irishgoodbye.io/widget.js"></script>`;

    return (
        <div className="min-h-screen-safe bg-[#050505]">
            <header className="border-b border-white/5 sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-[#D4AF37]" />
                        <span className="font-serif italic text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Consent Commerce</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-[#9CA3AF] hidden sm:inline">{settings?.business_name || user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-12 py-8 space-y-6">
                {/* View Tabs */}
                <div className="flex gap-2 border-b border-white/5 pb-4">
                    {["leads", "campaigns", "setup", "embed"].map((v) => (
                        <button key={v} onClick={() => setView(v)}
                            className={`px-4 py-2 text-sm rounded-md transition-colors ${view === v ? "bg-[#047857] text-white" : "text-[#9CA3AF] hover:text-white hover:bg-white/5"}`}
                            data-testid={`view-${v}`}
                        >
                            {v === "leads" ? `Leads (${leads.length})` : v === "campaigns" ? `Renewals (${expiringLeads.length} expiring)` : v === "setup" ? "Settings" : "Embed Widget"}
                        </button>
                    ))}
                </div>

                {/* ========== LEADS VIEW ========== */}
                {view === "leads" && (
                    <>
                        <div className="flex items-center justify-between">
                            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Verified Leads</h1>
                            <Button variant="outline" size="sm" onClick={exportCSV} className="border-white/10" data-testid="export-csv"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <StatCard label="Total" value={leads.length} />
                            <StatCard label="Tier 1" value={leads.filter(l => l.tier === 1).length} />
                            <StatCard label="Tier 2" value={leads.filter(l => l.tier === 2).length} />
                            <StatCard label="Tier 3" value={leads.filter(l => l.tier === 3).length} accent="gold" />
                            <StatCard label="AI Agents" value={leads.filter(l => l.is_ai_agent).length} accent="blue" />
                        </div>

                        {leads.length > 0 ? (
                            <div className="space-y-2" data-testid="leads-list">
                                {leads.map((lead) => (
                                    <Card key={lead.id} className="bg-[#0a0a0a] border-white/5 hover:border-white/10 transition-colors">
                                        <CardContent className="p-4 flex items-start justify-between">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <p className="text-sm font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>{lead.name || lead.email}</p>
                                                    <Badge className={`text-[10px] ${statusColors[lead.status] || statusColors.new}`}>{lead.status}</Badge>
                                                    <Badge variant="outline" className="text-[10px] border-white/10">Tier {lead.tier}</Badge>
                                                    {lead.discount_description && <Badge className="text-[10px] bg-[#047857]/20 text-[#047857]">{lead.discount_description}</Badge>}
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-[#6B7280] flex-wrap">
                                                    <span>{lead.email}</span>
                                                    {lead.phone && <span>{lead.phone}</span>}
                                                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Expires {new Date(lead.opt_in_expires).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                            {lead.is_ai_agent && <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px] shrink-0 ml-2">AI Agent</Badge>}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="bg-[#0a0a0a] border-white/5">
                                <CardContent className="p-12 text-center space-y-4">
                                    <Users className="h-12 w-12 text-[#D4AF37] mx-auto opacity-50" />
                                    <h3 className="text-lg font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>No leads yet</h3>
                                    <p className="text-sm text-[#9CA3AF]">Go to the <button onClick={() => setView("embed")} className="text-[#047857] underline">Embed Widget</button> tab to add the consent widget to your website.</p>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}

                {/* ========== CAMPAIGNS VIEW ========== */}
                {view === "campaigns" && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Consent Renewals</h1>
                                <p className="text-[#9CA3AF] mt-1">Re-engage leads before their consent expires. Stay compliant, stay in touch.</p>
                            </div>
                            <Button onClick={createCampaign} className="bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)]" data-testid="create-campaign-btn">
                                <Zap className="h-4 w-4 mr-2" />New Campaign
                            </Button>
                        </div>

                        {/* Expiring Alert */}
                        {expiringLeads.length > 0 && (
                            <Card className="bg-[#D4AF37]/5 border-[#D4AF37]/20" data-testid="expiring-alert">
                                <CardContent className="p-4 flex items-center gap-3">
                                    <Clock className="h-5 w-5 text-[#D4AF37] shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium text-[#D4AF37]">{expiringLeads.length} leads expiring in the next 60 days</p>
                                        <p className="text-xs text-[#9CA3AF]">Send a renewal campaign to keep them opted in. Offer a small incentive in exchange for continued consent.</p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Best Practice Tip */}
                        <Card className="bg-[#047857]/5 border-[#047857]/20">
                            <CardContent className="p-4 text-xs text-[#9CA3AF] space-y-2">
                                <p className="text-[#047857] font-medium text-sm">Best Practices for Consent Renewal</p>
                                <p>Send renewal offers 30 days before expiry — gives the consumer time to act without urgency pressure.</p>
                                <p>Offer something tangible: $10 off a service, free accessory, exclusive access. The value exchange must be clear.</p>
                                <p>California (CCPA) and TCPA require explicit opt-in. Irish Goodbye renewal creates a timestamped, auditable consent record that satisfies compliance.</p>
                                <p>Average renewal rate with an offer: 35-45%. Without an offer: 8-12%.</p>
                            </CardContent>
                        </Card>

                        {/* Campaigns List */}
                        {campaigns.length > 0 ? (
                            <div className="space-y-3" data-testid="campaigns-list">
                                {campaigns.map((c) => (
                                    <Card key={c.id} className="bg-[#0a0a0a] border-white/5">
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <p className="text-sm font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>{c.name}</p>
                                                        <Badge className={c.active ? "bg-[#047857]/20 text-[#047857] text-[10px]" : "bg-[#6B7280]/20 text-[#6B7280] text-[10px]"}>
                                                            {c.active ? "Active" : "Paused"}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-xs text-[#D4AF37] mb-2">Offer: {c.renewal_offer || "No offer set"}</p>
                                                    <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                                                        <span>Trigger: {c.trigger_days_before_expiry} days before expiry</span>
                                                        <span>New window: {c.renewal_window_days} days</span>
                                                        <span>Tiers: {(c.target_tiers || []).join(", ")}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-2 text-xs">
                                                        <span className="text-[#9CA3AF]">Sent: {c.sends || 0}</span>
                                                        <span className="text-[#047857]">Renewed: {c.renewals || 0}</span>
                                                        {c.sends > 0 && (
                                                            <span className="text-[#D4AF37]">{Math.round((c.renewals || 0) / c.sends * 100)}% conversion</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <Button size="sm" onClick={() => sendCampaign(c.id)} disabled={sendingCampaign === c.id}
                                                    className="bg-[#047857] text-white hover:bg-[#036046] h-8 shrink-0" data-testid={`send-campaign-${c.id}`}>
                                                    {sendingCampaign === c.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Zap className="h-3 w-3 mr-1" />}
                                                    Send Now
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="bg-[#0a0a0a] border-white/5">
                                <CardContent className="p-12 text-center space-y-4">
                                    <Clock className="h-12 w-12 text-[#D4AF37] mx-auto opacity-50" />
                                    <h3 className="text-lg font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>No campaigns yet</h3>
                                    <p className="text-sm text-[#9CA3AF]">Create your first consent renewal campaign to re-engage expiring leads.</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}

                {/* ========== SETUP VIEW ========== */}
                {view === "setup" && settings && (
                    <div className="space-y-6">
                        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Business Settings</h1>

                        {/* Business Info */}
                        <Card className="bg-[#0a0a0a] border-white/5">
                            <CardHeader><CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Business Info</CardTitle></CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-xs text-[#6B7280] mb-1 block">Business Name</label>
                                    <input value={settings.business_name || ""} onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
                                        onBlur={() => saveSettings({ business_name: settings.business_name })}
                                        className="w-full h-10 px-3 rounded-md bg-[#0a0a0a] border border-white/10 text-sm text-white focus:outline-none focus:border-[#047857]"
                                        placeholder="Your Company" data-testid="business-name-input" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-[#6B7280] mb-1 block">Listing Name</label>
                                        <input value={settings.listing_name || ""} onChange={(e) => setSettings({ ...settings, listing_name: e.target.value })}
                                            onBlur={() => saveSettings({ listing_name: settings.listing_name })}
                                            className="w-full h-10 px-3 rounded-md bg-[#0a0a0a] border border-white/10 text-sm text-white focus:outline-none focus:border-[#047857]"
                                            placeholder="e.g. 2025 Toyota Camry" data-testid="listing-name-input" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-[#6B7280] mb-1 block">List Price ($)</label>
                                        <input type="number" value={settings.list_price || ""} onChange={(e) => setSettings({ ...settings, list_price: parseFloat(e.target.value) || 0 })}
                                            onBlur={() => saveSettings({ list_price: settings.list_price })}
                                            className="w-full h-10 px-3 rounded-md bg-[#0a0a0a] border border-white/10 text-sm text-white focus:outline-none focus:border-[#047857]"
                                            placeholder="29,999" data-testid="list-price-input" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Industry Template */}
                        <Card className="bg-[#0a0a0a] border-white/5">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }} className="flex items-center gap-2"><Zap className="h-5 w-5 text-[#D4AF37]" />Industry Template</CardTitle>
                                <CardDescription>Select your industry — we'll auto-configure tiers, discounts, and marketing window</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex flex-wrap gap-2">
                                    {["automotive", "real_estate", "travel", "boats", "ecommerce"].map((ind) => (
                                        <Button key={ind} size="sm" onClick={() => applyTemplate(ind)}
                                            className={settings.industry === ind ? "bg-[#047857] text-white" : "bg-white/5 text-[#9CA3AF] hover:text-white"} data-testid={`industry-${ind}`}>
                                            {ind.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                                        </Button>
                                    ))}
                                </div>
                                {bestPractices && (
                                    <div className="p-3 rounded-md bg-[#047857]/5 border border-[#047857]/20 text-xs text-[#9CA3AF]" data-testid="best-practice-tip">
                                        <span className="text-[#047857] font-medium">Best Practice:</span> {bestPractices.reason}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Marketing Window */}
                        <Card className="bg-[#0a0a0a] border-white/5">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Marketing Opt-In Window</CardTitle>
                                <CardDescription>How long the consumer agrees to be contacted</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Select value={String(settings.marketing_window_days)} onValueChange={(v) => saveSettings({ marketing_window_days: parseInt(v) })}>
                                    <SelectTrigger data-testid="window-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="30">30 days</SelectItem>
                                        <SelectItem value="60">60 days</SelectItem>
                                        <SelectItem value="90">90 days (recommended for auto)</SelectItem>
                                        <SelectItem value="120">120 days</SelectItem>
                                        <SelectItem value="180">180 days (recommended for real estate)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </CardContent>
                        </Card>

                        {/* Tier Preview */}
                        <Card className="bg-[#0a0a0a] border-white/5">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Tier Configuration</CardTitle>
                                <CardDescription>What consumers unlock at each tier</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {(settings.tiers || []).map((tier, i) => (
                                    <div key={i} className="p-4 rounded-md bg-black/30 border border-white/5">
                                        <div className="flex items-center justify-between mb-2">
                                            <Badge variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37]">Tier {tier.tier}</Badge>
                                            {tier.discount_type !== "none" && tier.discount_value > 0 && (
                                                <Badge className="bg-[#047857]/20 text-[#047857] text-xs">
                                                    {tier.discount_type === "percentage" ? `${tier.discount_value}% off` : `$${tier.discount_value} off`}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-white mb-1">{tier.unlock || tier.description}</p>
                                        <p className="text-xs text-[#6B7280]">Fields: {(tier.fields || tier.data_required || []).join(", ")}</p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ========== EMBED VIEW ========== */}
                {view === "embed" && (
                    <div className="space-y-6">
                        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Embed Widget</h1>

                        {/* Steps */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[
                                { step: "1", title: "Copy the code", desc: "One snippet — paste it anywhere on your site" },
                                { step: "2", title: "Customize in Settings", desc: "Set your industry, tiers, and discounts" },
                                { step: "3", title: "Collect leads", desc: "Verified, opted-in leads appear here instantly" },
                            ].map((s) => (
                                <Card key={s.step} className="bg-[#0a0a0a] border-white/5">
                                    <CardContent className="p-4">
                                        <span className="text-3xl font-bold text-white/5" style={{ fontFamily: "'Playfair Display', serif" }}>{s.step}</span>
                                        <h3 className="text-sm font-medium mt-2" style={{ fontFamily: "Manrope, sans-serif" }}>{s.title}</h3>
                                        <p className="text-xs text-[#6B7280] mt-1">{s.desc}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {/* Embed Code */}
                        <Card className="bg-[#0a0a0a] border-[#D4AF37]/20">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }} className="flex items-center gap-2"><Code className="h-5 w-5 text-[#D4AF37]" />Your Embed Code</CardTitle>
                                <CardDescription>Add this to any HTML page — Shopify, WordPress, Squarespace, Wix, custom sites, dealer pages</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="relative">
                                    <pre className="p-4 rounded-lg bg-black/50 border border-white/5 text-xs text-[#9CA3AF] font-mono overflow-x-auto whitespace-pre-wrap" data-testid="embed-code">
                                        {embedCode}
                                    </pre>
                                    <Button size="sm" onClick={copyEmbed} className="absolute top-2 right-2 bg-[#047857] text-white hover:bg-[#036046] h-8" data-testid="copy-embed-btn">
                                        <Copy className="h-3 w-3 mr-1" />Copy
                                    </Button>
                                </div>

                                <div className="space-y-2 text-xs text-[#6B7280]">
                                    <p className="font-medium text-white">Works with:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {["Shopify", "WordPress", "Squarespace", "Wix", "Webflow", "Custom HTML", "Dealer.com", "Cars.com widgets", "Any website"].map((p) => (
                                            <Badge key={p} variant="outline" className="border-white/10 text-[10px]">{p}</Badge>
                                        ))}
                                    </div>
                                </div>

                                <div className="p-3 rounded-md bg-[#047857]/5 border border-[#047857]/20">
                                    <p className="text-xs text-[#9CA3AF]">
                                        <span className="text-[#047857] font-medium">Shopify:</span> Go to Online Store → Themes → Edit Code → Add the snippet to your product template or a custom page.
                                    </p>
                                    <p className="text-xs text-[#9CA3AF] mt-2">
                                        <span className="text-[#047857] font-medium">WordPress:</span> Add a Custom HTML block in any page/post and paste the code.
                                    </p>
                                    <p className="text-xs text-[#9CA3AF] mt-2">
                                        <span className="text-[#047857] font-medium">Any site:</span> Paste the snippet where you want the widget to appear. That's it.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Theme Options */}
                        <Card className="bg-[#0a0a0a] border-white/5">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: "Manrope, sans-serif" }}>Theme Options</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <p className="text-xs text-[#6B7280]">Change <code className="text-[#047857]">data-theme</code> in the embed code:</p>
                                <div className="flex gap-4">
                                    <div className="p-3 rounded-md bg-black border border-white/10 text-center flex-1">
                                        <p className="text-xs text-white font-medium">data-theme="dark"</p>
                                        <p className="text-[10px] text-[#6B7280]">Dark background</p>
                                    </div>
                                    <div className="p-3 rounded-md bg-white border border-gray-200 text-center flex-1">
                                        <p className="text-xs text-gray-900 font-medium">data-theme="light"</p>
                                        <p className="text-[10px] text-gray-500">Light background</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </main>
        </div>
    );
}

function StatCard({ label, value, accent }) {
    const color = accent === "gold" ? "text-[#D4AF37]" : accent === "blue" ? "text-blue-400" : "";
    return (
        <Card className="bg-[#0a0a0a] border-white/5">
            <CardContent className="p-4">
                <span className="text-xs text-[#6B7280]">{label}</span>
                <p className={`text-2xl font-bold ${color}`} style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
            </CardContent>
        </Card>
    );
}
